import { useEffect, useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/lib/CartContext";
import { Reveal } from "@/components/Reveal";

export default function PlansGrid({ kind = "hosting", cta = "Get this plan" }) {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("year");
  const { openWith } = useCart();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(kind === "hosting" ? data.hosting : data.email)).catch(() => {});
  }, [kind]);

  return (
    <section className="relative section-pad" data-testid={`plans-${kind}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-end mb-8">
          <div className="inline-flex rounded-full border border-[color:var(--border)] p-1">
            {["month", "year"].map((v) => (
              <button
                key={v}
                onClick={() => setCycle(v)}
                className={`px-4 h-9 rounded-full text-[12px] font-mono uppercase tracking-widest transition-colors ${
                  cycle === v ? "bg-[color:var(--text)] text-[color:var(--bg)]" : "text-[color:var(--text-muted)]"
                }`}
                data-testid={`plans-cycle-${v}`}
              >
                {v}ly {v === "year" && <span className="normal-case text-brand-primary">· save 17%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p, i) => {
            const price = cycle === "month" ? p.price_month_gbp : p.price_year_gbp;
            const featured = i === 1;
            return (
              <Reveal key={p.id} delay={i * 0.06}>
                <div
                  className={`card-surface p-8 lg:p-10 h-full flex flex-col relative ${
                    featured ? "border-brand-primary/60" : ""
                  }`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {featured && (
                    <span className="absolute top-6 right-6 h-6 px-2.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest bg-brand-primary text-white grid place-items-center">
                      Popular
                    </span>
                  )}
                  <div>
                    <h3 className="font-serif text-2xl tracking-tight">{p.name}</h3>
                    <p className="text-[13px] text-[color:var(--text-muted)] mt-1.5">{p.tagline}</p>
                  </div>
                  <div className="my-8">
                    <div className="font-serif text-5xl tracking-tight">£{price}</div>
                    <div className="text-[11.5px] text-[color:var(--text-muted)] font-mono uppercase tracking-widest mt-1">
                      per {cycle}
                    </div>
                  </div>
                  <ul className="space-y-2.5 text-[13.5px] mb-8 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={14} className="text-brand-primary shrink-0 mt-1" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() =>
                      openWith(kind === "hosting" ? { hostingPlanId: p.id } : { emailPlanId: p.id })
                    }
                    className={`h-11 px-5 rounded-full font-semibold text-[13px] inline-flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90 ${
                      featured
                        ? "bg-brand-primary text-white"
                        : "bg-[color:var(--text)] text-[color:var(--bg)]"
                    }`}
                    data-testid={`plan-cta-${p.id}`}
                  >
                    {cta} <ArrowRight size={14} />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
